<template>
  <div class="archive-wrap" @keydown.native="onKeyNav">
    <header class="arch-top">
      <div class="search-wrap">
        <input
          v-model="q"
          class="search"
          :placeholder="searchPlaceholder"
          @input="onSearch"
        />
        <div v-if="activeTag" class="active-tag" @click="clearTag">
          #{{ activeTag }} ✕
        </div>
      </div>

      <div class="meta">
        <span>{{ filtered.length }} {{ filtered.length === 1 ? 'conversation' : 'conversations' }}</span>
        <button class="btn ghost" @click="refresh" :disabled="loading">{{ loading ? '…' : '↻ Refresh' }}</button>
      </div>
    </header>

    <div class="arch-grid">
      <!-- LEFT: LIST -->
      <aside class="list">
        <div class="list-head">
          <label class="chk">
            <input type="checkbox" :checked="allVisibleChecked" @change="toggleAllVisible($event)">
            <span>Select all (visible)</span>
          </label>
          <div class="bulk-actions" v-if="checkedIds.length">
            <button class="btn warn" @click="confirmBulkDelete">🗑 Delete {{ checkedIds.length }}</button>
            <button class="btn ghost" @click="clearChecks">Clear</button>
          </div>
        </div>

        <article
          v-for="c in filtered"
          :key="c.id"
          :class="['card', {active: current && current.id === c.id, checked: checkedIds.indexOf(c.id) >= 0}]"
          @click="open(c.id)"
        >
          <div class="row">
            <label class="chk" @click.stop>
              <input type="checkbox" :checked="checkedIds.indexOf(c.id)>=0" @change="toggleCheck(c.id, $event)">
            </label>

            <div class="title-wrap">
              <b class="title" :title="c.title || fallbackTitle(c)">{{ c.title || fallbackTitle(c) }}</b>
              <small class="date">{{ fmt(c.updatedAt || c.createdAt) }}</small>
            </div>

            <div class="row-actions" @click.stop>
              <button class="icon" title="Export JSON" @click="exportOne(c)">⇩</button>
              <button class="icon danger" title="Delete" @click="confirmDelete(c)">🗑</button>
            </div>
          </div>

          <p class="summary">{{ c.summary || autoSummary(c) }}</p>

          <div class="tags" v-if="(c.keywords || []).length">
            <button
              v-for="k in (c.keywords || [])"
              :key="k"
              class="tag"
              @click.stop="filterByTag(k)"
              :aria-pressed="String(activeTag === k)"
            >#{{ k }}</button>
          </div>

          <small class="counts">{{ (c.messages || []).length }} messages</small>
        </article>

        <div v-if="!loading && !list.length" class="empty-list muted">
          Nothing saved yet.
        </div>
      </aside>

      <!-- RIGHT: DETAIL -->
      <section class="detail" v-if="current">
        <header class="detail-head">
          <div class="detail-titles">
            <h4>{{ current.title || fallbackTitle(current) }}</h4>
            <small class="muted">{{ fmt(current.createdAt || current.updatedAt) }}</small>
          </div>
          <div class="detail-actions">
            <button class="btn ghost" @click="exportOne(current)">⇩ Export</button>
            <button class="btn warn" @click="confirmDelete(current)">🗑 Delete</button>
          </div>
        </header>

        <div class="chip-row" v-if="(current.keywords || []).length">
          <button
            v-for="k in (current.keywords || [])"
            :key="k"
            class="chip"
            @click="filterByTag(k)"
          >#{{ k }}</button>
        </div>

        <div class="summary-box">
          <h5>AI Summary</h5>
          <p>{{ current.summary || autoSummary(current) }}</p>
        </div>

        <div class="thread" ref="thread">
          <div
            v-for="(m,i) in (current.messages || [])"
            :key="i"
            :class="['msg', m.role]"
          >
            <p>{{ m.content }}</p>
          </div>
        </div>
      </section>

      <section class="detail empty" v-else>
        <p class="muted">Select a conversation from the left to view details.</p>
      </section>
    </div>

    <div v-if="error" class="error">⚠️ {{ error }}</div>
  </div>
</template>

<script>
// Uses the same ArchiveAPI your dashboard already calls.
import { ArchiveAPI as RealArchive } from "../utils/storage";
const ArchiveAPI = RealArchive || {};

export default {
  name: "ArchiveView",
  data() {
    return {
      q: "",
      activeTag: "",
      list: [],
      current: null,
      error: "",
      loading: false,
      checkedIds: [],
      keyIndex: -1, // for keyboard nav within filtered
      searchDebounce: null,
    };
  },
  computed: {
    searchPlaceholder() {
      return this.activeTag
        ? `Filtering by #${this.activeTag} — type to search within`
        : "Search archive by title, text, or keyword…";
    },
    filtered() {
      const q = this.q.trim().toLowerCase();
      const tag = (this.activeTag || "").trim().toLowerCase();
      let arr = this.list;

      if (tag) {
        arr = arr.filter(c => (c.keywords || []).map(x => String(x).toLowerCase()).includes(tag));
      }
      if (!q) return arr;

      return arr.filter((c) => {
        const hay = [
          c.title,
          c.summary,
          ...(c.keywords || []),
          ...((c.messages || []).map(m => m.content) || []),
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      });
    },
    allVisibleChecked() {
      if (!this.filtered.length) return false;
      return this.filtered.every(c => this.checkedIds.indexOf(c.id) >= 0);
    },
  },
  watch: {
    filtered() {
      // Keep keyIndex within bounds
      if (this.keyIndex >= this.filtered.length) this.keyIndex = this.filtered.length - 1;
      if (this.keyIndex < -1) this.keyIndex = -1;
    }
  },
  mounted() {
    this.refresh();
    window.addEventListener("archive:changed", this.refresh);
    window.addEventListener("storage", this.onStorage);
  },
  beforeDestroy() {
    window.removeEventListener("archive:changed", this.refresh);
    window.removeEventListener("storage", this.onStorage);
  },
  methods: {
    onSearch() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        // no-op: computed handles it, debounce keeps it snappy
      }, 120);
    },
    filterByTag(k) {
      this.activeTag = k;
    },
    clearTag() {
      this.activeTag = "";
    },
    onStorage(e) {
      // naive cross-tab signal, only act if key could belong to your store
      if (!e || !e.key) return;
      if (String(e.key).toLowerCase().includes("archive")) {
        this.refresh();
      }
    },
    async refresh() {
      this.error = "";
      this.loading = true;
      try {
        if (ArchiveAPI && typeof ArchiveAPI.list === "function") {
          const arr = await ArchiveAPI.list();
          this.list = Array.isArray(arr) ? arr.slice().sort(sortByTimeDesc) : [];
        } else {
          this.list = [];
        }

        // keep selection if possible, otherwise first visible
        if (this.current) {
          const again = this.list.find((c) => c.id === this.current.id);
          this.current = again || (this.filtered[0] || this.list[0] || null);
        } else {
          this.current = this.filtered[0] || this.list[0] || null;
        }

        // clamp keyboard index
        this.keyIndex = Math.max(-1, Math.min(this.keyIndex, this.filtered.length - 1));
      } catch (e) {
        this.error = e && e.message ? e.message : "Failed to load archive.";
      } finally {
        this.loading = false;
      }
    },
    open(id) {
      const c = this.list.find((x) => x.id === id);
      if (c) {
        this.current = c;
        // also move keyIndex to this in filtered
        const idx = this.filtered.findIndex(x => x.id === id);
        this.keyIndex = idx;
        this.$nextTick(() => this.scrollDetailToBottomIfNearEnd());
      }
    },
    scrollDetailToBottomIfNearEnd() {
      const el = this.$refs.thread;
      if (!el) return;
      // if already near bottom, keep it pinned after updates
      const near = (el.scrollHeight - el.scrollTop - el.clientHeight) < 120;
      if (near) el.scrollTop = el.scrollHeight;
    },
    fallbackTitle(c) {
      const m = (c && c.messages) || [];
      return (m[0] && m[0].content) ? truncate(m[0].content, 64) : "Untitled conversation";
    },
    autoSummary(c) {
      const m = (c && c.messages) || [];
      const joined = m.map(x => x.role + ": " + (x.content || "")).join(" ");
      return truncate(joined || "No content", 140);
    },
    fmt(ts) {
      try {
        const d = new Date(ts || Date.now());
        return d.toLocaleString();
      } catch { return ""; }
    },

    // ---- Selection helpers (bulk) ----
    toggleCheck(id, ev) {
      const on = ev && ev.target && ev.target.checked;
      const i = this.checkedIds.indexOf(id);
      if (on && i < 0) this.checkedIds.push(id);
      if (!on && i >= 0) this.checkedIds.splice(i, 1);
    },
    toggleAllVisible(ev) {
      const on = ev && ev.target && ev.target.checked;
      const ids = this.filtered.map(c => c.id);
      if (on) {
        // Add any missing visible ids
        const set = new Set(this.checkedIds.concat(ids));
        this.checkedIds = Array.from(set);
      } else {
        // Remove visible ids
        this.checkedIds = this.checkedIds.filter(id => ids.indexOf(id) < 0);
      }
    },
    clearChecks() {
      this.checkedIds = [];
    },

    // ---- Delete (single / bulk) ----
    confirmDelete(c) {
      const ok = window.confirm(`Delete “${c.title || this.fallbackTitle(c)}”? This can’t be undone.`);
      if (!ok) return;
      this.deleteOne(c.id);
    },
    async deleteOne(id) {
      this.error = "";
      try {
        // Prefer API if present
        if (ArchiveAPI && typeof ArchiveAPI.remove === "function") {
          await ArchiveAPI.remove(id);
        } else if (ArchiveAPI && typeof ArchiveAPI.delete === "function") {
          await ArchiveAPI.delete(id);
        } else if (ArchiveAPI && typeof ArchiveAPI.destroy === "function") {
          await ArchiveAPI.destroy(id);
        } else {
          // Soft fallback: remove from local list (in case of read-only demo)
          this.list = this.list.filter(c => c.id !== id);
        }

        // Update selection sensibly
        if (this.current && this.current.id === id) {
          // pick next sibling in filtered, otherwise previous, otherwise null
          const idx = this.filtered.findIndex(x => x.id === id);
          // Temporarily refresh list to re-evaluate filtered selection
          await this.refresh();
          const next = this.filtered[idx] || this.filtered[idx - 1] || null;
          this.current = next;
        } else {
          await this.refresh();
        }

        // signal same-tab listeners
        window.dispatchEvent(new CustomEvent("archive:changed"));
      } catch (e) {
        this.error = e && e.message ? e.message : "Failed to delete.";
      }
    },
    confirmBulkDelete() {
      if (!this.checkedIds.length) return;
      const ok = window.confirm(`Delete ${this.checkedIds.length} selected conversation(s)? This can’t be undone.`);
      if (!ok) return;
      this.deleteMany(this.checkedIds.slice());
    },
    async deleteMany(ids) {
      for (const id of ids) {
        try {
          if (ArchiveAPI && typeof ArchiveAPI.remove === "function") {
            await ArchiveAPI.remove(id);
          } else if (ArchiveAPI && typeof ArchiveAPI.delete === "function") {
            await ArchiveAPI.delete(id);
          } else if (ArchiveAPI && typeof ArchiveAPI.destroy === "function") {
            await ArchiveAPI.destroy(id);
          } else {
            this.list = this.list.filter(c => c.id !== id);
          }
        } catch (e) {
          this.error = `Error deleting ${id}: ${e && e.message ? e.message : e}`;
        }
      }
      this.checkedIds = [];
      await this.refresh();
      window.dispatchEvent(new CustomEvent("archive:changed"));
    },

    // ---- Export ----
    exportOne(c) {
      const blob = new Blob([JSON.stringify(c, null, 2)], { type: "application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
const safe = (c.title || this.fallbackTitle(c)).replace(/[^\w-]+/g, "_").slice(0, 64);
      a.href = url;
      a.download = `${safe || "conversation"}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },

    // ---- Keyboard navigation (list) ----
    onKeyNav(e) {
      // Only use when focus is not inside an input/textarea/select
      const tag = (e.target && e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (!this.filtered.length) return;
        this.keyIndex = Math.min(this.keyIndex + 1, this.filtered.length - 1);
        const item = this.filtered[this.keyIndex];
        if (item) this.open(item.id);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (!this.filtered.length) return;
        this.keyIndex = Math.max(this.keyIndex - 1, 0);
        const item = this.filtered[this.keyIndex];
        if (item) this.open(item.id);
      } else if (e.key === "Enter") {
        if (this.keyIndex >= 0 && this.filtered[this.keyIndex]) {
          this.open(this.filtered[this.keyIndex].id);
        }
      }
    },
  },
};

function sortByTimeDesc(a, b) {
  const ta = +new Date(a.updatedAt || a.createdAt || 0);
  const tb = +new Date(b.updatedAt || b.createdAt || 0);
  return tb - ta;
}
function truncate(str, n) {
  if (!str) return "";
  return str.length > n ? str.slice(0, n - 1) + "…" : str;
}
</script>

<style scoped>
/* Layout */
.archive-wrap{ display:flex; flex-direction:column; height:100%; }
.arch-top{
  display:flex; align-items:center; gap:12px;
  padding:10px 12px; border-bottom:1px solid rgba(255,255,255,.12);
  background: linear-gradient(180deg, rgba(0,224,255,.08), transparent 70%);
}
.search-wrap{ position:relative; flex:1; display:flex; align-items:center; gap:8px; }
.search{
  flex:1; padding:11px 12px; border-radius:12px; outline:none; font-size:14px;
  background: rgba(255,255,255,.06); color:#fff; border:1px solid rgba(255,255,255,.14);
}
.active-tag{
  white-space:nowrap; font-size:12px; padding:6px 10px; border-radius:999px; cursor:pointer;
  background: rgba(124,108,255,.18); border:1px solid rgba(124,108,255,.35);
}
.meta{ display:flex; align-items:center; gap:8px; }
.btn{ padding:6px 12px; border-radius:10px; background:#2a2a45; color:#fff; border:1px solid transparent; }
.btn.ghost{ border-color: rgba(255,255,255,.2); background:rgba(255,255,255,.06); }
.btn.ghost:hover{ background:rgba(0,224,255,.12); }
.btn.warn{ background: rgba(255, 88, 88, .1); border-color: rgba(255, 88, 88, .4); }
.btn.warn:hover{ background: rgba(255, 88, 88, .18); }

.arch-grid{
  display:grid; grid-template-columns: 420px 1fr; gap:0; flex:1; min-height:0;
}

/* List pane */
.list{ overflow:auto; padding:12px; border-right:1px solid rgba(255,255,255,.12); }
.list-head{
  position: sticky; top: 0; z-index: 2;
  display:flex; align-items:center; justify-content:space-between; gap:10px;
  padding:8px 6px 10px; margin-bottom:8px;
  background: linear-gradient(180deg, rgba(10,10,22,.9), rgba(10,10,22,.65));
  backdrop-filter: blur(4px);
  border-bottom:1px solid rgba(255,255,255,.08);
}
.chk { display:flex; align-items:center; gap:8px; font-size:12px; opacity:.9; }
.bulk-actions{ display:flex; align-items:center; gap:6px; }

.card{
  position:relative;
  background: rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.12);
  border-radius:12px; padding:10px; margin-bottom:10px; cursor:pointer;
  transition: transform .15s, background .15s, border-color .15s, box-shadow .15s;
}
.card:hover{ transform: translateY(-1px); background: rgba(0,224,255,.06); }
.card.active{ box-shadow:0 0 0 2px #00e0ff66 inset; }
.card.checked{ box-shadow:0 0 0 2px rgba(124,108,255,.5) inset; }

.row{ display:flex; justify-content:space-between; align-items:center; gap:8px; }
.title-wrap{ display:flex; flex-direction:column; gap:2px; min-width:0; flex:1; }
.title{ display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.date{ opacity:.8; }
.row-actions{ display:flex; gap:6px; opacity:0; transition:opacity .12s; }
.card:hover .row-actions{ opacity:1; }
.icon{
  border:1px solid rgba(255,255,255,.2); border-radius:8px; padding:4px 8px; background:rgba(255,255,255,.06); color:#fff;
}
.icon.danger{ border-color: rgba(255, 88, 88, .4); }

.summary{ opacity:.95; margin:6px 0; }
.tags{ display:flex; gap:6px; flex-wrap:wrap; margin-bottom:6px; }
.tag{
  padding:2px 8px; border-radius:999px; font-size:12px; cursor:pointer;
  background: rgba(124,108,255,.18); border:1px solid rgba(124,108,255,.35);
}
.counts{ opacity:.8; }

/* Detail pane */
.detail{ overflow:auto; padding:14px 16px; }
.detail.empty{ display:flex; align-items:center; justify-content:center; color:#cfd0ff; }
.detail-head{ display:flex; justify-content:space-between; align-items:flex-start; gap:8px; margin-bottom:10px; }
.detail-titles h4{ margin:0 0 2px; }
.detail-head .muted{ opacity:.8; }
.detail-actions{ display:flex; gap:6px; }

.chip-row{ display:flex; gap:6px; flex-wrap:wrap; margin-bottom:8px; }
.chip{
  padding:4px 8px; border-radius:10px; font-size:12px; cursor:pointer;
  background: rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.12);
}

.summary-box{
  margin:12px 0; padding:10px; border-radius:12px;
  background: rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.12);
}
.thread{ display:flex; flex-direction:column; gap:8px; }
.msg{ max-width: 88%; padding:10px 12px; border-radius:12px; background: rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.08); }
.msg.user{ align-self:flex-end; background:#4e2df214; border-color:#7c6cff66; }
.msg.assistant{ align-self:flex-start; }

.empty-list{ padding:24px; text-align:center; }
.muted{ opacity:.75; }
.error{ padding:10px; color:#ffd9e3; }

@media (max-width: 1080px){ .arch-grid{ grid-template-columns: 1fr; } }
</style>
